import type { PaginatedResponse, User } from '@/api/admin/users';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ProfileUsersModalProps {
  open: boolean;
  onClose: () => void;
  profileName: string;
  usersData: PaginatedResponse<User> | undefined;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

function UsersTableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map((key) => (
        <div key={key} className="flex gap-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      ))}
    </div>
  );
}

export function ProfileUsersModal({
  open,
  onClose,
  profileName,
  usersData,
  currentPage,
  onPageChange,
  isLoading,
}: ProfileUsersModalProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Usuários vinculados ao perfil - {profileName}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <UsersTableSkeleton />
            </div>
          ) : usersData && usersData.docs.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>E-mail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usersData.docs.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-sm text-gray-500">{user.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={usersData.pages || 1}
                totalItems={usersData.total || 0}
                limit={usersData.limit || 10}
                onPageChange={onPageChange}
              />
            </div>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              Nenhum usuário vinculado a este perfil.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
